import { FC, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import { reportIssueSuccessMessage } from 'dashboard/lib/constants/reportIssue';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Inquiry } from 'dashboard/models/inquiry';
import { Verification } from 'dashboard/models/verification';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  useGetTransactionQuery,
  useRedactTransactionMutation,
  useUpdateTransactionStatusMutation,
  useUpdateTransactionTagsMutation,
} from 'dashboard/redux/api/transaction';
import { RootState } from 'dashboard/redux/store';
import { loadRedactedObjectPaths } from 'dashboard/redux/thunk-actions/events';
import { flagInquiry } from 'dashboard/redux/thunk-actions/inquiry';
import { flagVerification } from 'dashboard/redux/thunk-actions/verification';
import { mapRelatedTransactionData } from 'dashboard/routes/DashboardTransactionsShowRoute/util';
import DashboardTransactionsShowView from 'dashboard/views/DashboardTransactionsShowView';
import { EventName } from 'lib/constants/events';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const nullStateMessages: Record<number, string> = {
  401: "You don't have permissions to view this Transaction.",
  404: 'We couldn’t find that Transaction.',
};

type Params = { transactionId: string };

const DashboardTransactionsShowRoute: FC<PropsFromRedux> = ({
  flagInquiry: flagInquiryAction,
  flagVerification: flagVerificationAction,
  loadRedactedObjectPaths: loadRedactedObjectPathsAction,
  redactedObjectPaths: allRedactedObjectPaths,
  showMessage,
}) => {
  useRedirectOnEnvironmentChange('/dashboard/transactions');

  const { transactionId: txnIdFromRoute } = useParams<Params>() as Params;

  const errorCode = useRef<number | null>(null);
  // Initially load transaction from transaction or reference-id param. Then load all transaction's dependencies
  // after.
  const {
    data: transactionData,
    isLoading: isLoadingTransaction,
    error: loadTransactionError,
  } = useGetTransactionQuery(txnIdFromRoute, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, isLoading, error }) => ({
      data: data ? { transaction: data.data, ...mapRelatedTransactionData(data) } : null,
      isLoading,
      error,
    }),
  });
  useRTKErrorHandler({
    error: loadTransactionError,
    onError: () => ({ message: 'Failed to load transaction.', type: MessageType.Failure }),
  });

  const {
    cases = [],
    currentEnvironment = null,
    redactor = null,
    relatedAccounts = [],
    relatedCases = [],
    relatedInquiries = [],
    relatedReports = [],
    relatedTransactions = [],
    relatedVerifications = [],
    transactionLabel = null,
    transactionType = null,
    transactionStatuses = [],
    relations = {},
    transactionTags = [],
    transaction,
    lists,
    listItems,
    listMatches,
  } = transactionData ?? {};

  const { loading: isLoadingRedactedObjectPaths, callback: loadRedactedObjectPaths } = useApiLoader(
    {
      loader: () =>
        loadRedactedObjectPathsAction(
          transaction == null ? [] : [transaction.id],
          EventName.TransactionRedacted,
        ),
      deps: [transaction],
      onError: (err: ApiResponseError) => {
        if (isPermissionsError(err)) {
          showMessage(permissionsErrorMessage(err), MessageType.Failure);
        } else {
          showMessage('Failed to load redaction data.', MessageType.Failure);
        }

        errorCode.current = err.status;
      },
      onNetworkError: () =>
        showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
    },
  );

  const [updateTransactionTags, { error: updateTransactionTagsError }] =
    useUpdateTransactionTagsMutation();
  useRTKErrorHandler({
    error: updateTransactionTagsError,
    onError: () => ({ message: 'Failed to add tags to transaction.', type: MessageType.Failure }),
  });
  const handleUpdateTransactionTags = async ({ tags }: FormUpdateTagsData) => {
    await updateTransactionTags({ id: txnIdFromRoute, tags });
    showMessage('Tags updated.', MessageType.Success);
    await loadRedactedObjectPaths();
  };

  const [updateTransactionStatus, { error: updateTransactionStatusError }] =
    useUpdateTransactionStatusMutation();
  useRTKErrorHandler({
    error: updateTransactionStatusError,
    onError: () => ({
      message: 'Failed to update transaction status. Please try again.',
      type: MessageType.Failure,
    }),
  });
  const handleUpdateTransactionStatus = async (transactionStatusId: string) => {
    await updateTransactionStatus({ id: txnIdFromRoute, transactionStatusId });
    showMessage('Transaction status updated.', MessageType.Success);
    await loadRedactedObjectPaths();
  };

  const [redactTransaction, { error: redactTransactionError }] = useRedactTransactionMutation();
  useRTKErrorHandler({
    error: redactTransactionError,
    onError: () => ({ message: 'Failed to redact transaction.', type: MessageType.Failure }),
  });
  const handleRedactTransaction = async (transactionId: string) => {
    await redactTransaction(transactionId);
    showMessage('Successfully redacted transaction.', MessageType.Success);
    await loadRedactedObjectPaths();
  };

  const { error: handleFlagVerificationIssuesError, callback: handleFlagVerificationIssues } =
    useApiCallback(
      async (verification: Verification, values: FlagIssuesFormData) => {
        const { issueVerifications, issueType, responseType, content, addAsFraudulent } = values;
        await flagVerificationAction(verification, {
          issueVerifications,
          issueType,
          responseType,
          content,
          addAsFraudulent,
        });
        showMessage(reportIssueSuccessMessage(responseType), MessageType.Success);
      },
      [flagVerificationAction],
    );

  useApiErrorHandler({
    error: handleFlagVerificationIssuesError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to report issue, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('Failed to report issue, please try again.', MessageType.Failure),
  });

  const { error: handleFlagInquiryIssuesError, callback: handleFlagInquiryIssues } = useApiCallback(
    async (inquiry: Inquiry, verifications: Verification[], values: FlagIssuesFormData) => {
      const { issueVerifications, issueType, responseType, content, addAsFraudulent } = values;
      await flagInquiryAction(inquiry, verifications, {
        issueVerifications,
        issueType,
        responseType,
        content,
        addAsFraudulent,
      });
      showMessage(reportIssueSuccessMessage(responseType), MessageType.Success);
    },
    [flagInquiryAction],
  );

  useApiErrorHandler({
    error: handleFlagInquiryIssuesError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to report issue, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('Failed to report issue, please try again.', MessageType.Failure),
  });

  return (
    <DashboardTransactionsShowView
      cases={cases}
      currentEnvironment={currentEnvironment}
      isLoading={isLoadingTransaction || isLoadingRedactedObjectPaths}
      nullStateMessage={nullStateMessages[errorCode.current ?? 0] ?? ''}
      redactedObjectPaths={transaction == null ? [] : allRedactedObjectPaths[transaction.id]}
      redactor={redactor}
      redactTransaction={handleRedactTransaction}
      relatedAccounts={relatedAccounts}
      relatedCases={relatedCases}
      relatedInquiries={relatedInquiries}
      relatedReports={relatedReports}
      relatedTransactions={relatedTransactions}
      relatedVerifications={relatedVerifications}
      relations={relations}
      transaction={transaction?.id === txnIdFromRoute ? transaction : null}
      transactionLabel={transactionLabel}
      transactionStatuses={transactionStatuses}
      transactionType={transactionType}
      transactionTags={transactionTags}
      lists={lists ?? {}}
      listItems={listItems ?? {}}
      listMatches={listMatches ?? {}}
      handleFlagInquiryIssues={handleFlagInquiryIssues}
      handleFlagVerificationIssues={handleFlagVerificationIssues}
      handleUpdateTags={handleUpdateTransactionTags}
      handleUpdateTransactionStatus={handleUpdateTransactionStatus}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  redactedObjectPaths: state.events.redactedObjectPaths,
});

const mapDispatchToProps = {
  flagInquiry,
  flagVerification,
  loadRedactedObjectPaths,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardTransactionsShowRoute);
